import { BaseTutorialCard, useTutorial, useTutorialStyles, useWindowSize } from '@capsicum/common-web';
import { type MaskStylesObj } from '@reactour/mask';
import { type PopoverStylesObj } from '@reactour/popover';
import { type StylesObj } from '@reactour/tour';
import { useEffect, useRef } from 'react';
import { useTheme } from 'styled-components';

export const useAuctionDetailsTutorial = (bidComponentReady?: boolean) => {
	const placeBidRef = useRef<HTMLButtonElement>(null);

	const { breakpoints, spacings } = useTheme();
	const {
		dimensions: { width }
	} = useWindowSize();

	const { highlightedAreaStyle, popoverStyles } = useTutorialStyles();
	const { setup } = useTutorial();

	useEffect(() => {
		if (!bidComponentReady) return;

		//#region styles and position definiton
		const popoverPadding = parseInt(spacings.xxlarge.replace('px', ''));
		const maskPadding = parseInt(spacings.xxxsmall.replace('px', ''));
		const baseRx = parseInt(spacings.large.replace('px', ''));

		const padding = {
			mask: maskPadding,
			popover: popoverPadding
		};

		const popoverPosition = (data: {
			height: number;
			width: number;
			windowHeight: number;
			windowWidth: number;
			x: number;
			y: number;
		}): [number, number] => {
			const { height, width, windowWidth, x, y } = data;

			if (windowWidth < breakpoints.points.mobile.pageWidth) {
				return [windowWidth - width - x, y - height - popoverPadding];
			}

			return [x - (width + popoverPadding) / 2, y - height - popoverPadding];
		};

		const styles: StylesObj & PopoverStylesObj & MaskStylesObj = {
			clickArea: base => ({
				...base,
				rx: baseRx
			}),
			highlightedArea: base => ({
				...highlightedAreaStyle({ disableInteraction: true })?.(base),
				rx: baseRx
			}),
			maskArea: base => ({
				...base,
				rx: baseRx
			}),
			popover: (base, state) => {
				return {
					...base,
					...popoverStyles()?.({ ...base, marginLeft: 6 }, state)
				};
			}
		};
		//#endregion

		if (placeBidRef.current) {
			setup('auction-details', {
				preventBeginnerText: width < breakpoints.points.mobile.pageWidth,
				steps: [
					{
						content: () => (
							<BaseTutorialCard
								content="rewards.auctions.tutorials.details.steps.first.content"
								title="rewards.auctions.tutorials.details.steps.first.title"
							/>
						),
						padding,
						position: popoverPosition,
						selector: placeBidRef?.current ?? '',
						stepInteraction: false,
						styles
					},
					{
						content: () => (
							<BaseTutorialCard
								content="rewards.auctions.tutorials.details.steps.second.content"
								title="rewards.auctions.tutorials.details.steps.second.title"
							/>
						),
						padding,
						position: popoverPosition,
						selector: placeBidRef?.current ?? '',
						stepInteraction: false,
						styles
					}
				]
			});
		}
		//#endregion
	}, [bidComponentReady, width]);

	return {
		placeBidRef
	};
};
